import { tagged } from "@mirage/service-logging";
import { useMediaQuery } from "@react-hookz/web";
import { atom, useAtom } from "jotai";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { useActionEvents } from "./bridgeObservers";

// keep in consistent with the FSS themes metaserver/static/js/hooks/constants/dark-setting.ts
export const FSS_THEMES = ["dark", "bright", "os"];

export const fssThemeDataAtom = atom<string>("os");

const logger = tagged("fssTheme");

export const useFssTheme = () => {
  const isDarkOS = useMediaQuery("(prefers-color-scheme: dark)") ?? false;
  const [isDarkMode, setIsDarkMode] = useState(isDarkOS);
  const [fssTheme, setFssTheme] = useAtom(fssThemeDataAtom);
  const { search } = useLocation();
  const actionEvent = useActionEvents("update.theme");

  const params = useMemo(() => new URLSearchParams(search), [search]);
  const fssThemeParam = params.get("theme");

  const applyFssTheme = useCallback(
    (fssThemeParam: string): void => {
      if (FSS_THEMES.includes(fssThemeParam.toLowerCase())) {
        logger.info(`Setting Chat embed theme to FSS theme: ${fssThemeParam}`);
        setFssTheme(fssThemeParam.toLowerCase());
      } else {
        logger.warn(
          `Received unsupported theme from URL: ${fssThemeParam}. Valid themes are: ${FSS_THEMES.join(", ")}`,
        );
      }
    },
    [setFssTheme],
  );

  useEffect(() => {
    if (!fssThemeParam) return;
    applyFssTheme(fssThemeParam);
  }, [fssThemeParam, applyFssTheme]);

  useEffect(() => {
    if (fssTheme === "os") {
      setIsDarkMode(isDarkOS);
    } else if (fssTheme === "dark") {
      setIsDarkMode(true);
    } else if (fssTheme === "bright") {
      setIsDarkMode(false);
    }
  }, [fssTheme, isDarkOS, setIsDarkMode]);

  useEffect(() => {
    if (!actionEvent) return;

    if (actionEvent.data) {
      const theme = actionEvent.data.darkSetting;
      applyFssTheme(theme);
    }
  }, [actionEvent, applyFssTheme]);

  return {
    isDarkMode,
  };
};
